/**
 * Shop the Collection Carousel Component
 * Allows users to click thumbnails to update and display different products from the collection
 */

class ShopTheCollectionCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentProductIndex = 0;
    this.products = [];
    this.currentVariant = null;
    this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
  }

  connectedCallback() {
    // Extract products data from data attribute or parse from DOM
    this.initializeProducts();
    this.setupEventListeners();
    this.setupVariantChangeListener();
    this.renderInitialProduct();

    // Dispatch ready event so parent can attach form listeners
    this.dispatchEvent(new CustomEvent('shop-the-collection:ready', { bubbles: true }));
  }

  /**
   * Initialize products array from the script tag
   */
  initializeProducts() {
    // Try to get products from script tag first
    const scriptId = this.getAttribute('data-products-script-id');
    if (scriptId) {
      const scriptElement = document.getElementById(scriptId);
      if (scriptElement) {
        try {
          this.products = JSON.parse(scriptElement.textContent);
          return;
        } catch (e) {
          console.error('Failed to parse products data from script tag:', e);
        }
      }
    }

    // Fallback: try data attribute for backwards compatibility
    const productsData = this.getAttribute('data-products');
    if (productsData) {
      try {
        this.products = JSON.parse(productsData);
      } catch (e) {
        console.error('Failed to parse products data:', e);
        return;
      }
    }
  }

  /**
   * Setup event listeners on thumbnail buttons
   */
  setupEventListeners() {
    const thumbnails = this.querySelectorAll('.shop-the-collection__thumbnail');
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleThumbnailClick(index);
      });
    });

    // Listen for size/option selection changes
    this.setupSizeOptionListener();
  }

  /**
   * Setup listener for size option selections
   */
  setupSizeOptionListener() {
    // Listen to ALL option inputs (size, color, etc.) in the options container
    const optionsContainer = this.querySelector('.shop-the-collection__product-options');
    if (!optionsContainer) return;

    const inputs = optionsContainer.querySelectorAll('input[data-single-option-selector]');
    inputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        this.handleSizeOptionChange(e);
      });
    });
  }

  /**
   * Handle size option change and find matching variant
   * @param {Event} e - The change event
   */
  handleSizeOptionChange(e) {
    const selectedInput = e.target;
    const selectedSize = selectedInput.value;
    if (!selectedSize) return;

    // Get the current product
    const product = this.products[this.currentProductIndex];
    if (!product || !product.variants) return;

    // Get selected options using the event target to get the most current value
    const selectedOptions = this.getSelectedOptionsWithTarget(selectedInput);

    // Find variant that matches all selected options
    const matchingVariant = product.variants.find((variant) => {
      return selectedOptions.every((selectedValue, index) => {
        return variant.options[index] && 
               variant.options[index].toLowerCase() === selectedValue.toLowerCase();
      });
    });

    if (matchingVariant) {
      this.currentVariant = matchingVariant;
      this.updateFormWithVariant(matchingVariant);

      // Update price if available
      const priceElement = this.querySelector('.shop-the-collection__product-price');
      if (priceElement && matchingVariant.price) {
        priceElement.textContent = this.formatPrice(matchingVariant.price);
      }

      // Update option value displays
      this.updateOptionValueDisplays(matchingVariant);
    } else {
      console.warn('No matching variant found for selected options');
    }
  }

  /**
   * Get selected options including the target input that just changed
   * @param {HTMLElement} targetInput - The input that triggered the change
   * @return {Array} Array of selected option values in order
   */
  getSelectedOptionsWithTarget(targetInput) {
    const selectedOptions = [];
    const form = this.querySelector('.shop-the-collection__product-form');
    if (!form) return selectedOptions;

    // Get all option selectors and sort by their index to maintain order
    const inputs = Array.from(form.querySelectorAll('input[data-single-option-selector]'));
    
    // Group inputs by their data-index (option position)
    const inputsByIndex = {};
    inputs.forEach((input) => {
      const index = input.getAttribute('data-index')?.replace('option', '') || '0';
      if (!inputsByIndex[index]) {
        inputsByIndex[index] = [];
      }
      inputsByIndex[index].push(input);
    });

    // For each option position, find the checked/selected value
    Object.keys(inputsByIndex).sort((a, b) => parseInt(a) - parseInt(b)).forEach((index) => {
      const optionInputs = inputsByIndex[index];
      
      // If one of these inputs is the target, use its value
      // Otherwise, find the checked input
      let selectedValue = null;

      const targetInGroup = optionInputs.find((input) => input === targetInput);
      if (targetInGroup) {
        selectedValue = targetInGroup.value;
      } else {
        const checkedInput = optionInputs.find((input) => input.checked);
        if (checkedInput) {
          selectedValue = checkedInput.value;
        }
      }

      if (selectedValue) {
        selectedOptions.push(selectedValue);
      }
    });

    return selectedOptions;
  }

  /**
   * Setup listener for variant selection changes
   */
  setupVariantChangeListener() {
    // Listen for variant changes from product-variant-options
    // Use arrow function to maintain proper 'this' context
    this._variantChangeListener = (e) => this.handleVariantChange(e);
    document.addEventListener('theme:variant:change', this._variantChangeListener);
  }

  /**
   * Handle variant selection changes
   * @param {Event} e - The variant change event
   */
  handleVariantChange(e) {
    const { variant } = e.detail;
    if (!variant) return;

    // Check if the event came from within our drawer
    const drawer = document.querySelector('[data-shop-the-collection-drawer-body]');
    if (!drawer) return;

    // Check if the event target is within the drawer
    if (e.target && !drawer.contains(e.target)) {
      return;
    }

    // Update the variant
    this.currentVariant = variant;
    this.updateFormWithVariant(variant);
    
    // Update price if available
    const priceElement = this.querySelector('.shop-the-collection__product-price');
    if (priceElement && variant.price) {
      priceElement.textContent = this.formatPrice(variant.price);
    }

    // Update option value displays
    this.updateOptionValueDisplays(variant);
  }

  /**
   * Update the displayed option values in the variant selector
   * @param {Object} variant - The selected variant
   */
  updateOptionValueDisplays(variant) {
    if (!variant.options) return;

    // Update each option value display
    const optionValueElements = this.querySelectorAll('[data-option-value]');
    optionValueElements.forEach((element, index) => {
      if (variant.options[index]) {
        element.textContent = variant.options[index];
      }
    });
  }

  /**
   * Clear all option value displays
   */
  clearOptionValueDisplays() {
    const optionValueElements = this.querySelectorAll('[data-option-value]');
    optionValueElements.forEach((element) => {
      element.textContent = '';
    });
  }

  /**
   * Update form input with selected variant ID
   * @param {Object} variant - The selected variant
   */
  updateFormWithVariant(variant) {
    if (!variant || !variant.id) return;

    // Find the form by looking for the shop-the-collection__product-form
    const form = this.querySelector('.shop-the-collection__product-form');
    if (!form) {
      console.warn('Could not find shop-the-collection__product-form');
      return;
    }

    // Update the variant ID input
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
    } else {
      console.warn('Could not find input[name="id"] in form');
    }

    // Update button disabled state based on variant availability
    this.updateAddToCartButton(variant);
  }

  /**
   * Update the add to cart button's disabled state
   * @param {Object} variant - The selected variant
   */
  updateAddToCartButton(variant) {
    const form = this.querySelector('.shop-the-collection__product-form');
    if (!form) return;

    const submitButton = form.querySelector('button[type="submit"]');
    if (!submitButton) return;

    // Check if a radio input is currently checked
    const checkedInput = form.querySelector('input[data-single-option-selector]:checked');
    
    // Button is enabled only if:
    // 1. There's a checked input AND
    // 2. The checked input is NOT disabled AND
    // 3. There's an available variant
    const isButtonEnabled = checkedInput && !checkedInput.disabled && variant && variant.available;

    if (isButtonEnabled) {
      submitButton.disabled = false;
      submitButton.classList.remove('disabled');
      // Restore original button text
      const originalText = submitButton.getAttribute('data-original-text');
      if (originalText) {
        submitButton.textContent = originalText;
      }
    } else {
      submitButton.disabled = true;
      submitButton.classList.add('disabled');
      // Store original text if not already stored
      if (!submitButton.getAttribute('data-original-text')) {
        submitButton.setAttribute('data-original-text', submitButton.textContent);
      }
      // Change text to "Select an Option" using the translated text from the form
      const selectOptionText = form.getAttribute('data-select-option-text') || 'Select an Option';
      submitButton.textContent = selectOptionText;
    }
  }

  /**
   * Handle thumbnail click and update displayed product
   * @param {number} index - The index of the clicked thumbnail
   */
  handleThumbnailClick(index) {
    if (index === this.currentProductIndex) return; // No change if same product

    this.currentProductIndex = index;
    this.updateProductDisplay();
    this.updateThumbnailState();
  }

  /**
   * Update the product display section with new product data
   */
  updateProductDisplay() {
    if (!this.products[this.currentProductIndex]) return;

    const product = this.products[this.currentProductIndex];
    
    // Show loader overlay for 1 second
    this.showDrawerLoader();
    
    // Find the first available variant, fall back to first variant
    let selectedVariant = null;
    if (product.variants) {
      selectedVariant = product.variants.find((variant) => variant.available) || product.variants[0];
    }
    
    // Store current variant
    this.currentVariant = selectedVariant;
    
    // Update product title
    const titleElement = this.querySelector('.shop-the-collection__product-title-text');
    if (titleElement) {
      titleElement.textContent = product.title;
    }

    // Update product title
    const titleLinkElement = this.querySelector('.shop-the-collection__product-title-link');
    if (titleLinkElement) {
      titleLinkElement.href = product.url;
    }

    // Update product price
    const priceElement = this.querySelector('.shop-the-collection__product-price');
    if (priceElement && product.price) {
      priceElement.textContent = this.formatPrice(product.price);
    }

    // Update product description
    const descriptionElement = this.querySelector('.shop-the-collection__product-description .toggle-ellipsis__content');
    if (descriptionElement && product.description) {
      descriptionElement.innerHTML = product.description;
    }

    // Update link to product page
    const linkElement = this.querySelector('.shop-the-collection__link-to-product');
    if (linkElement) {
      // Try multiple possible URL properties
      let productUrl = product.url || product.handle || '';
      
      // If we only have the handle, construct the full URL
      if (productUrl && !productUrl.startsWith('/')) {
        productUrl = '/products/' + productUrl;
      }
      
      if (productUrl) {
        linkElement.href = productUrl;
      }
    }

    // Update sibling color hidden input
    this.updateSiblingColorInput(product);

    // Update sibling products section with new product HTML
    this.updateSiblingProductsHTML(product);

    // Update product ratings with new product ID
    this.updateProductRatings(product);

    // Clear the form - don't pre-select any variant, let user choose
    this.selectVariantInForm(null);

    // Update product gallery images
    this.updateProductGallery(product);

    // Update product variant options if needed
    this.updateProductOptions(product);

    // Trigger custom event for external listeners
    this.dispatchEvent(
      new CustomEvent('shop-the-collection:product-changed', {
        detail: {
          product: product,
          variant: selectedVariant,
          index: this.currentProductIndex,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Select a variant's options in the form
   * @param {Object} variant - The variant to select
   */
  selectVariantInForm(variant) {
    const form = this.querySelector('.shop-the-collection__product-form');
    if (!form) return;

    // Clear the variant ID input - user must select an option
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = '';
    }

    // Don't auto-check any options - let user choose manually
    const allInputs = form.querySelectorAll('input[data-single-option-selector]');
    allInputs.forEach((input) => {
      input.checked = false;
    });

    // Clear all option value displays until user selects one
    this.clearOptionValueDisplays();

    // Disable button - user must click an available option to enable it
    this.updateAddToCartButton(null);
  }

  /**
   * Update the sibling color hidden input when product changes
   * @param {Object} product - The product object
   */
  updateSiblingColorInput(product) {
    const form = this.querySelector('.shop-the-collection__product-form');
    if (!form) return;

    // Get the sibling color directly from the product
    const siblingColor = product.sibling_color || '';
    
    if (!siblingColor) return;

    // Update the hidden properties input
    const propertiesInputs = form.querySelectorAll('.shop-the-collection__sibling-products input[type="hidden"]');
    propertiesInputs.forEach((input) => {
      if (input.name.includes('Color') || input.name.includes('Colour')) {
        input.value = siblingColor;
      }
    });

    // Update the .radio__legend__value text
    const radioLegendValue = form.querySelector('.shop-the-collection__sibling-products .radio__legend__value');
    if (radioLegendValue) {
      radioLegendValue.textContent = siblingColor;
    }
  }

  /**
   * Update the sibling products section with new product HTML
   * @param {Object} product - The product object
   */
  updateSiblingProductsHTML(product) {
    const siblingProductsContainer = this.querySelector('.shop-the-collection__sibling-products');
    if (!siblingProductsContainer) return;

    // Get the section ID from the products script tag
    const productsScriptId = this.getAttribute('data-products-script-id');
    if (!productsScriptId) return;

    // Extract section ID from script ID (format: shop-the-collection-products-{sectionId})
    const sectionId = productsScriptId.replace('shop-the-collection-products-', '');
    
    // Get the stored sibling HTML for this product
    const siblingHtmlScriptId = `shop-the-collection-sibling-html-${sectionId}-${product.id}`;
    const siblingHtmlScript = document.getElementById(siblingHtmlScriptId);
    
    if (!siblingHtmlScript) {
      console.warn(`Sibling HTML not found for product ${product.id}`);
      return;
    }

    // Get the HTML content from the script tag
    const siblingHTML = siblingHtmlScript.innerHTML;
    
    // Clear the container and insert new HTML
    siblingProductsContainer.innerHTML = siblingHTML;

    // Reinitialize any event listeners on the new elements if needed
    // (e.g., if product-siblings snippet has interactive elements)
  }

  /**
   * Update the product ratings section by updating the product ID
   * @param {Object} product - The product object
   */
  updateProductRatings(product) {
    const ratingsContainer = this.querySelector('.shop-the-collection__product-ratings');
    if (!ratingsContainer) return;

    const newProductId = `shopify-${product.id}`;

    // Find or create the rating div
    let ratingDiv = ratingsContainer.querySelector('[data-oke-star-rating]');
    
    // Update the product ID attribute using the OKE API
    if (window.okeWidgetApi && typeof window.okeWidgetApi.setProduct === 'function') {
      window.okeWidgetApi.setProduct(ratingDiv, newProductId);
    } else if (window.okeWidgetApi && typeof window.okeWidgetApi.forceReinitialization === 'function') {
      // Fallback: force reinitialization if setProduct doesn't exist
      window.okeWidgetApi.forceReinitialization();
    }

    // Prevent OKE review buttons from triggering actions in the drawer
    setTimeout(() => {
      const clickableButtons = ratingsContainer.querySelectorAll('.oke-is-clickable');
      clickableButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
        }, true); // Use capture phase to intercept before other handlers
      });
    }, 100);
  }

  /**
   * Update the product gallery slider with new images
   * @param {Object} product - The product object
   */
  updateProductGallery(product) {
    const galleryContainer = this.querySelector('.shop-the-collection__product-gallery');
    if (!galleryContainer || !product.images) return;

    const sliderComponent = galleryContainer.querySelector('[data-slider]');
    if (!sliderComponent) return;

    // Destroy existing Flickity instance
    if (sliderComponent.flkty) {
      sliderComponent.flkty.destroy();
      sliderComponent.flkty = null;
      sliderComponent.classList.remove('flickity-enabled');
    }

    // Clear existing slides
    const slides = sliderComponent.querySelectorAll('[data-slide]');
    slides.forEach((slide) => slide.remove());

    // Add new slides for the selected product
    const images = [];
    product.images.forEach((image, index) => {
      const slideDiv = document.createElement('div');
      slideDiv.setAttribute('data-slide', `slide-${index}`);

      const img = document.createElement('img');
       // Determine image source safely, handling both string URLs and objects
      let imageSrc = '';
      if (image && typeof image === 'object') {
        imageSrc = image.src || image.url || '';
      } else if (typeof image === 'string') {
        imageSrc = `${image}&width=200`;
      }
      if (!imageSrc) {
        // Skip this image if we cannot determine a valid source
        return;
      }
      img.src = imageSrc;
      img.alt = (image && typeof image === 'object' && image.alt) || product.title;

      slideDiv.appendChild(img);
      sliderComponent.appendChild(slideDiv);
      images.push(img);
    });

    // Wait for all images to load before reinitializing Flickity
    this.waitForImagesToLoad(images).then(() => {
      this.reinitializeFlickity(sliderComponent);
    }).catch((error) => {
      console.warn('Error loading images:', error);
      // Still reinitialize Flickity even if some images failed to load
      this.reinitializeFlickity(sliderComponent);
    });
  }

  /**
   * Wait for all images to load
   * @param {Array} images - Array of img elements
   * @return {Promise} Resolves when all images are loaded
   */
  waitForImagesToLoad(images) {
    if (images.length === 0) {
      return Promise.resolve();
    }

    const promises = images.map((img) => {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error(`Image load timeout: ${img.src}`));
        }, 10000); // 10 second timeout per image

        const cleanup = () => {
          clearTimeout(timeout);
        };

        if (img.complete) {
          cleanup();
          resolve();
        } else {
          img.addEventListener('load', () => {
            cleanup();
            resolve();
          }, { once: true });

          img.addEventListener('error', () => {
            cleanup();
            reject(new Error(`Image failed to load: ${img.src}`));
          }, { once: true });
        }
      });
    });

    return Promise.all(promises);
  }

  /**
   * Show loader overlay on the drawer for 1 second
   */
  showDrawerLoader() {
    // Find the drawer element
    const drawer = document.querySelector('#shop-the-collection-drawer');
    if (!drawer) return;

    // Create or get the loader element
    let loader = drawer.querySelector('.shop-the-collection__loader');

    // Show the loader
    loader.style.display = 'flex';

    // Hide after 1 second
    setTimeout(() => {
      loader.style.display = 'none';
    }, 750);
  }

  /**
   * Reinitialize Flickity carousel
   * @param {HTMLElement} sliderComponent - The slider-component element
   */
  reinitializeFlickity(sliderComponent) {
    if (!sliderComponent) return;

    // Get options from data attribute or use defaults
    let options = {};
    const dataOptions = sliderComponent.getAttribute('data-options');
    if (dataOptions) {
      try {
        options = JSON.parse(dataOptions);
      } catch (e) {
        console.warn('Failed to parse data-options:', e);
      }
    }

    // Use theme's Flickity class if available, otherwise use window.Flickity
    if (window.theme && window.theme.Flickity) {
      sliderComponent.flkty = new window.theme.Flickity(sliderComponent, options);
    } else if (window.Flickity) {
      sliderComponent.flkty = new window.Flickity(sliderComponent, options);
    } else {
      console.error('Flickity library is not loaded.');
    }
  }

  /**
   * Update product options (variants, etc.)
   * @param {Object} product - The product object
   */
  updateProductOptions(product) {
    const optionsContainer = this.querySelector('.shop-the-collection__product-options');
    if (!optionsContainer) return;

    // Update the variant options HTML for the selected product
    this.updateVariantOptionsHTML(product);

    // Update availability of variant options
    this.updateVariantAvailability(product);

    // Dispatch event to notify that options need to be updated
    // This allows external scripts to handle variant selection
    this.dispatchEvent(
      new CustomEvent('shop-the-collection:update-options', {
        detail: {
          product: product,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Update the variant options HTML when product changes
   * @param {Object} product - The product object
   */
  updateVariantOptionsHTML(product) {
    const variantOptionsContainer = this.querySelector('.shop-the-collection__variant-options');
    if (!variantOptionsContainer) return;

    // Get the section ID from the products script tag
    const productsScriptId = this.getAttribute('data-products-script-id');
    if (!productsScriptId) return;

    // Extract section ID from script ID (format: shop-the-collection-products-{sectionId})
    const sectionId = productsScriptId.replace('shop-the-collection-products-', '');
    
    // Get the stored variant options HTML for this product
    const variantOptionsScriptId = `shop-the-collection-variant-options-html-${sectionId}-${product.id}`;
    const variantOptionsScript = document.getElementById(variantOptionsScriptId);
    
    if (!variantOptionsScript) {
      console.warn(`Variant options HTML not found for product ${product.id}`);
      return;
    }

    // Get the HTML content from the script tag
    const variantOptionsHTML = variantOptionsScript.innerHTML;
    
    // Clear the container and insert new HTML
    variantOptionsContainer.innerHTML = variantOptionsHTML;

    // Re-setup size option listener for the new options
    this.setupSizeOptionListener();
  }

  /**
   * Update the disabled state of variant option inputs based on availability
   * @param {Object} product - The product object
   */
  updateVariantAvailability(product) {
    const form = this.querySelector('.shop-the-collection__product-form');
    if (!form || !product.variants) return;

    // Re-query inputs after DOM has been updated
    const inputs = form.querySelectorAll('input[data-single-option-selector]');
    if (inputs.length === 0) {
      console.warn('No variant option inputs found after HTML update');
      return;
    }

    // Get the option names (e.g., "Size", "Color") from the first available fieldset labels
    const availableOptions = this.getAvailableOptions(product);

    inputs.forEach((input) => {
      const inputValue = input.value;
      if (!inputValue) return;

      // Get which option position this input corresponds to
      const optionIndex = this.getOptionIndex(form, input);
      if (optionIndex === -1) {
        // If we can't determine the option index, assume it's available
        input.disabled = false;
        input.classList.remove('disabled');
        return;
      }

      // Check if any available variant has this exact option value at this position
      const hasAvailableVariant = product.variants.some((variant) => {
        return variant.available && variant.options[optionIndex] === inputValue;
      });

      // Disable if no available variant has this option at this position
      if (!hasAvailableVariant) {
        input.disabled = true;
        input.classList.add('disabled');
        input.checked = false;
      } else {
        input.disabled = false;
        input.classList.remove('disabled');
      }
    });

    // Check if there are any available variants at all
    const hasAnyAvailableVariant = product.variants.some((v) => v.available);
    
    // Only disable button if no available variants exist
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      if (!hasAnyAvailableVariant) {
        submitButton.disabled = true;
        submitButton.classList.add('disabled');
      }
    }
  }

  /**
   * Get all available option names for a product
   * @param {Object} product - The product object
   * @return {Array} Array of available option names (e.g., ["Size", "Color"])
   */
  getAvailableOptions(product) {
    // Shopify products have an options array on the product object
    // This would typically be ["Size", "Color", etc.]
    if (product.options && Array.isArray(product.options)) {
      return product.options;
    }

    // Fallback: derive from variant options (all variants should have same number of options)
    if (product.variants && product.variants.length > 0) {
      return product.variants[0].options.map((_, index) => `Option ${index + 1}`);
    }

    return [];
  }

  /**
   * Determine which option position an input corresponds to
   * @param {HTMLElement} form - The form element
   * @param {HTMLElement} input - The input element
   * @return {number} The option index (0-based), or -1 if not found
   */
  getOptionIndex(form, input) {
    // Look for the parent fieldset and count which fieldset this input belongs to
    const fieldsets = form.querySelectorAll('.radio__fieldset');
    let optionIndex = 0;

    for (let fieldset of fieldsets) {
      if (fieldset.contains(input)) {
        return optionIndex;
      }
      optionIndex++;
    }

    // Fallback: try to find by data attribute
    const optionAttr = input.getAttribute('data-option-position');
    if (optionAttr !== null) {
      return parseInt(optionAttr, 10);
    }

    return -1;
  }

  /**
   * Update visual state of thumbnails
   */
  updateThumbnailState() {
    const thumbnails = this.querySelectorAll('.shop-the-collection__thumbnail');
    thumbnails.forEach((thumbnail, index) => {
      if (index === this.currentProductIndex) {
        thumbnail.classList.add('is-active');
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.classList.remove('is-active');
        thumbnail.setAttribute('aria-current', 'false');
      }
    });
  }

  /**
   * Render the initial product on component load
   */
  renderInitialProduct() {
    if (this.products.length === 0) return;
    this.currentProductIndex = 0;
    this.updateProductDisplay();
    this.updateThumbnailState();
  }

  /**
   * Format price for display
   * @param {number} price - Price in cents or dollars
   * @return {string} Formatted price
   */
  formatPrice(price) {
    if (typeof price === 'string') {
      return price;
    }
    
    // Use theme's format money if available
    if (window.theme && window.theme.formatMoney) {
      return window.theme.formatMoney(price, window.theme.moneyFormat);
    }

    // Fallback formatting
    return `$${(price / 100).toFixed(2)}`;
  }

  /**
   * Set products data programmatically
   * @param {Array} products - Array of product objects
   */
  setProducts(products) {
    this.products = products;
    this.currentProductIndex = 0;
    this.setupEventListeners();
    this.renderInitialProduct();
  }

  /**
   * Cleanup when component is removed
   */
  disconnectedCallback() {
    if (this._variantChangeListener) {
      document.removeEventListener('theme:variant:change', this._variantChangeListener);
    }
  }

  /**
   * Get current active product
   * @return {Object} Current product object
   */
  getCurrentProduct() {
    return this.products[this.currentProductIndex];
  }

  /**
   * Get current product index
   * @return {number} Current index
   */
  getCurrentProductIndex() {
    return this.currentProductIndex;
  }

  /**
   * Get current selected variant
   * @return {Object} Current variant object
   */
  getCurrentVariant() {
    return this.currentVariant;
  }
}

// Register the web component
if (!customElements.get('shop-the-collection-carousel')) {
  customElements.define('shop-the-collection-carousel', ShopTheCollectionCarousel);
}
